/**
 * PeakPay Fast Checkout & Buy Now Integration
 * Handles modal, address validation, pincode lookup, and PeakPay API communication.
 */

(function () {
  'use strict';

  const PeakPayCheckout = {
    cartData: {
      items: [],
      totalPrice: 0
    },

    // Survives the round trip to PeakPay's hosted page and back.
    PENDING_KEY: 'peakpay_pending_payment',

    init() {
      this.bindElements();
      this.bindEvents();
      this.bindPincodeLookup();
      this.resumePendingPayment();
      console.log('[PeakPay] Checkout system initialized.');
    },

    /**
     * PeakPay sends the customer back here after paying. Without this the payment
     * succeeds and the store never hears about it — the webhook was the only path
     * that reached Shopify, and its payload carries no cart.
     */
    async resumePendingPayment() {
      let pending = null;
      try {
        pending = JSON.parse(localStorage.getItem(this.PENDING_KEY) || 'null');
      } catch (e) {}
      if (!pending || !pending.orderId) return;

      // One shot: clear first so a failed verify cannot re-fire on every page load.
      localStorage.removeItem(this.PENDING_KEY);

      const backendUrl = (window.PEAKPAY_CONFIG?.backendUrl || 'http://localhost:5000').replace(/\/$/, '');
      try {
        const res = await fetch(`${backendUrl}/api/verify-payment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(pending)
        });
        const result = await res.json();
        if (!result.success) {
          console.warn('[PeakPay] Payment not confirmed:', result.message);
          return;
        }
        this.cartData.totalPrice = Number(pending.totalPrice) || 0;
        this.showSuccess(result, pending.customer || {});
        // Order is placed — empty the Shopify cart so it cannot be paid for twice.
        await fetch('/cart/clear.js', { method: 'POST' });
      } catch (err) {
        console.error('[PeakPay] Could not verify payment on return:', err);
      }
    },

    bindElements() {
      this.modal = document.getElementById('peakpay-checkout-modal');
      this.successModal = document.getElementById('peakpay-success-modal');
      this.backdrop = this.modal?.querySelector('.peakpay-modal__backdrop');
      this.closeBtn = this.modal?.querySelector('.peakpay-modal__close');
      this.form = document.getElementById('peakpay-checkout-form');
      this.payBtn = document.getElementById('pp-pay-btn');
      this.summaryContainer = document.getElementById('pp-summary-items');
      this.subtotalEl = document.getElementById('pp-subtotal-val');
      this.totalEl = document.getElementById('pp-total-val');
    },

    bindEvents() {
      // Close modal on close button or backdrop click
      this.closeBtn?.addEventListener('click', () => this.close());
      this.backdrop?.addEventListener('click', () => this.close());

      // Intercept Buy Now buttons on Product page
      document.addEventListener('click', (e) => {
        const target = e.target.closest('[data-peakpay-buy-now], .shopify-payment-button__button, .btn-checkout-dynamic button, .button_buy-now button.btn-buy-now, .btn-peakpay-buy-now');
        if (target) {
          e.preventDefault();
          e.stopPropagation();
          this.handleBuyNowClick(target);
        }
      });

      // Handle Pay Button Click
      this.payBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        this.submitPayment();
      });

      // Escape key to close
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.modal?.classList.contains('is-open')) {
          this.close();
        }
      });
    },

    /**
     * Automatic Pincode lookup for India
     */
    bindPincodeLookup() {
      const pincodeInput = document.getElementById('pp-customer-pincode');
      const cityInput = document.getElementById('pp-customer-city');
      const stateInput = document.getElementById('pp-customer-state');

      if (!pincodeInput) return;

      pincodeInput.addEventListener('input', async (e) => {
        const val = e.target.value.trim();
        if (val.length === 6 && /^\d+$/.test(val)) {
          try {
            const res = await fetch(`https://api.postalpincode.in/pincode/${val}`);
            const data = await res.json();
            if (data && data[0] && data[0].Status === 'Success') {
              const postOffice = data[0].PostOffice[0];
              if (cityInput && !cityInput.value) cityInput.value = postOffice.District || postOffice.Block;
              if (stateInput && !stateInput.value) stateInput.value = postOffice.State;
            }
          } catch (err) {
            console.debug('[PeakPay Pincode Lookup]', err);
          }
        }
      });
    },

    /**
     * Gather product data or cart data and open modal
     */
    async handleBuyNowClick(btn) {
      // Check if clicked from a cart / minicart form
      const isCartBtn = btn.name === 'checkout' || btn.closest('#minicart') || btn.closest('.cart__checkout-button') || btn.classList.contains('btn-checkout');
      
      if (isCartBtn) {
        try {
          const res = await fetch('/cart.js');
          const cart = await res.json();
          if (cart.items && cart.items.length > 0) {
            const items = cart.items.map(item => ({
              variant_id: item.variant_id,
              title: `${item.product_title}${item.variant_title ? ' - ' + item.variant_title : ''}`,
              quantity: item.quantity,
              price: item.price / 100,
              image: item.image
            }));
            this.openCheckout({
              items,
              totalPrice: cart.total_price / 100
            });
            return;
          }
        } catch (err) {
          console.debug('[PeakPay] Failed to load cart.js, falling back to page product', err);
        }
      }

      const form = btn.closest('form[action*="/cart/add"]') || btn.closest('product-form') || document.querySelector('form[action*="/cart/add"]');
      
      let variantId = btn.dataset.variantId || null;
      let quantity = 1;
      let title = btn.dataset.productTitle || document.querySelector('.product-detail__title, .product__title h1, .product__title, .product-title, h1.product__name, h1')?.innerText?.trim() || 'Product';
      let price = parseFloat(btn.dataset.productPrice) || 0;
      let imageUrl = btn.dataset.productImage || document.querySelector('.product-detail__media img, .product__media img, .product-gallery img, media-gallery img')?.getAttribute('src') || '';

      if (form) {
        const variantInput = form.querySelector('[name="id"]');
        if (variantInput && variantInput.value) {
          variantId = variantInput.value;
        }
        const qtyInput = form.querySelector('[name="quantity"]') || document.querySelector('#quantity, input[name="quantity"]');
        if (qtyInput) quantity = parseInt(qtyInput.value, 10) || 1;
      }

      // Try reading updated product price from DOM (e.g. after variant change)
      const priceElement = document.querySelector('.product-detail__price .price, .card-product-price .price, [id^="price-"] .price, .price-item--sale, .price-item--regular, .price__regular .price-item, .product-price .price');
      if (priceElement) {
        const rawText = priceElement.innerText.replace(/,/g, '');
        const match = rawText.match(/\d+(?:\.\d+)?/);
        if (match && parseFloat(match[0]) > 0) {
          price = parseFloat(match[0]);
        }
      }

      // If price couldn't be read from DOM or dataset, try Shopify analytics / window data
      if (!price || price <= 0) {
        try {
          if (window.ShopifyAnalytics?.meta?.product?.price) {
            price = window.ShopifyAnalytics.meta.product.price / 100;
          }
        } catch (e) {}
      }

      if (!price || price <= 0) {
        price = 1999; // Store default fallback
      }

      this.openCheckout({
        items: [
          {
            variant_id: variantId,
            title: title,
            quantity: quantity,
            price: price,
            image: imageUrl
          }
        ],
        totalPrice: price * quantity
      });
    },

    /**
     * Open PeakPay modal with specified items
     */
    openCheckout({ items, totalPrice }) {
      this.cartData.items = items;
      this.cartData.totalPrice = totalPrice;

      this.renderSummary();
      if (this.modal) {
        this.modal.classList.add('is-open');
        this.modal.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
      }
    },

    close() {
      if (this.modal) {
        this.modal.classList.remove('is-open');
        this.modal.setAttribute('aria-hidden', 'true');
      }
      if (this.successModal) {
        this.successModal.classList.remove('is-open');
        this.successModal.setAttribute('aria-hidden', 'true');
      }
      document.body.style.overflow = '';
    },

    renderSummary() {
      if (!this.summaryContainer) return;

      const currencySymbol = window.PEAKPAY_CONFIG?.currencySymbol || '₹';
      let html = '';

      this.cartData.items.forEach((item) => {
        html += `
          <div class="pp-item-row">
            ${item.image ? `<img src="${item.image}" alt="${item.title}" class="pp-item-img" />` : ''}
            <div class="pp-item-info">
              <div class="pp-item-title">${item.title}</div>
              <div class="pp-item-qty">Qty: ${item.quantity}</div>
            </div>
            <div class="pp-item-price">${currencySymbol}${(item.price * item.quantity).toFixed(2)}</div>
          </div>
        `;
      });

      this.summaryContainer.innerHTML = html;
      if (this.subtotalEl) this.subtotalEl.innerText = `${currencySymbol}${this.cartData.totalPrice.toFixed(2)}`;
      if (this.totalEl) this.totalEl.innerText = `${currencySymbol}${this.cartData.totalPrice.toFixed(2)}`;
    },

    validateForm() {
      let isValid = true;

      const name = document.getElementById('pp-customer-name');
      const phone = document.getElementById('pp-customer-phone');
      const address = document.getElementById('pp-customer-address');
      const pincode = document.getElementById('pp-customer-pincode');

      // Validate Name
      if (!name.value.trim()) {
        this.showError('name', 'Please enter your full name');
        isValid = false;
      } else {
        this.clearError('name');
      }

      // Validate 10-digit Phone
      const cleanPhone = phone.value.replace(/\D/g, '');
      if (cleanPhone.length < 10) {
        this.showError('phone', 'Please enter a valid 10-digit mobile number');
        isValid = false;
      } else {
        this.clearError('phone');
      }

      // Validate Address
      if (!address.value.trim() || address.value.trim().length < 5) {
        this.showError('address', 'Please enter complete delivery address');
        isValid = false;
      } else {
        this.clearError('address');
      }

      // Validate Pincode
      const cleanPin = pincode.value.replace(/\D/g, '');
      if (cleanPin.length !== 6) {
        this.showError('pincode', 'Please enter a valid 6-digit pincode');
        isValid = false;
      } else {
        this.clearError('pincode');
      }

      return isValid;
    },

    showError(field, message) {
      const errEl = document.getElementById(`pp-error-${field}`);
      const inputEl = document.getElementById(`pp-customer-${field}`);
      if (errEl) errEl.innerText = message;
      if (inputEl) inputEl.classList.add('has-error');
    },

    clearError(field) {
      const errEl = document.getElementById(`pp-error-${field}`);
      const inputEl = document.getElementById(`pp-customer-${field}`);
      if (errEl) errEl.innerText = '';
      if (inputEl) inputEl.classList.remove('has-error');
    },

    async submitPayment() {
      if (!this.validateForm()) return;

      const customerData = {
        name: document.getElementById('pp-customer-name').value.trim(),
        phone: document.getElementById('pp-customer-phone').value.trim(),
        email: document.getElementById('pp-customer-email').value.trim() || 'customer@shopify.com',
        address: document.getElementById('pp-customer-address').value.trim(),
        pincode: document.getElementById('pp-customer-pincode').value.trim(),
        city: document.getElementById('pp-customer-city').value.trim(),
        state: document.getElementById('pp-customer-state').value.trim(),
        country: 'India'
      };

      // Save customer details in session
      try {
        sessionStorage.setItem('peakpay_customer', JSON.stringify(customerData));
      } catch (e) {}

      const backendUrl = (window.PEAKPAY_CONFIG?.backendUrl || 'http://localhost:5000').replace(/\/$/, '');

      try {
        console.log('[PeakPay] Generating new live payment link for amount ₹' + this.cartData.totalPrice);
        
        const response = await fetch(`${backendUrl}/api/create-order`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            items: this.cartData.items,
            customer: customerData,
            shippingAddress: customerData,
            totalPrice: this.cartData.totalPrice,
            // Where PeakPay sends the customer once the payment settles.
            returnUrl: window.location.origin + '/cart',
            notes: {
              customer_name: customerData.name,
              customer_phone: customerData.phone
            }
          })
        });

        const data = await response.json();

        if (data.success && data.paymentUrl) {
          console.log('[PeakPay] Fresh payment URL created:', data.paymentUrl);
          // Hold the cart across the redirect: the return trip needs it to build
          // the Shopify order, and PeakPay's own payload does not carry it.
          localStorage.setItem(this.PENDING_KEY, JSON.stringify({
            orderId: data.orderId,
            items: this.cartData.items,
            customer: customerData,
            shippingAddress: customerData,
            totalPrice: this.cartData.totalPrice
          }));
          window.location.href = data.paymentUrl;
          return;
        }

        throw new Error(data.message || 'Could not generate dynamic payment link');

      } catch (err) {
        console.warn('[PeakPay Notice]:', err);
        alert('PeakPay Error: Please ensure your peakpay-server backend is running to generate new payment links.');
        this.setLoading(false);
      }
    },

    setLoading(isLoading) {
      if (!this.payBtn) return;
      const textSpan = this.payBtn.querySelector('.pp-btn-text');
      const lockIcon = this.payBtn.querySelector('.pp-lock-icon');
      const spinner = this.payBtn.querySelector('.pp-btn-spinner');

      if (isLoading) {
        this.payBtn.disabled = true;
        if (textSpan) textSpan.innerText = 'Connecting to PeakPay...';
        if (lockIcon) lockIcon.style.display = 'none';
        if (spinner) spinner.removeAttribute('hidden');
      } else {
        this.payBtn.disabled = false;
        if (textSpan) textSpan.innerText = 'Pay Now via PeakPay';
        if (lockIcon) lockIcon.style.display = 'inline-block';
        if (spinner) spinner.setAttribute('hidden', '');
      }
    },

    showSuccess(orderResult, customer) {
      if (this.modal) this.modal.classList.remove('is-open');
      if (this.successModal) {
        const detailsEl = document.getElementById('pp-success-details-box');
        const currencySymbol = window.PEAKPAY_CONFIG?.currencySymbol || '₹';
        const orderId = orderResult?.shopifyOrder?.orderName || orderResult?.verification?.orderId || `PP-${Date.now()}`;

        if (detailsEl) {
          detailsEl.innerHTML = `
            <div style="margin-bottom: 6px;"><strong>Order ID:</strong> ${orderId}</div>
            <div style="margin-bottom: 6px;"><strong>Payment Gateway:</strong> PeakPay (Verified)</div>
            <div style="margin-bottom: 6px;"><strong>Recipient:</strong> ${customer.name} (${customer.phone})</div>
            <div style="margin-bottom: 6px;"><strong>Deliver to:</strong> ${customer.address}, ${customer.city} - ${customer.pincode}</div>
            <div><strong>Amount Paid:</strong> ${currencySymbol}${this.cartData.totalPrice.toFixed(2)}</div>
          `;
        }
        this.successModal.classList.add('is-open');
        this.successModal.setAttribute('aria-hidden', 'false');
      }
    }
  };

  // Expose to window
  window.PeakPayCheckout = PeakPayCheckout;

  // Auto initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => PeakPayCheckout.init());
  } else {
    PeakPayCheckout.init();
  }
})();
